import React, { useCallback, useEffect, useState } from 'react';

function formatCreators(handles) {
  const list = (handles || []).filter(Boolean).slice(0, 5);
  if (!list.length) return null;
  return list.map((h) => `@${String(h).replace(/^@/, '')}`).join(', ');
}

function BuyerQuestionSlide({ q, index, total }) {
  const creators = formatCreators(q.creator_handles);
  return (
    <div className="ph-comments-qmodal-slide">
      <p className="ph-comments-qmodal-counter">
        Question {index + 1} of {total}
      </p>
      <blockquote className="ph-comments-qmodal-quote">{q.question}</blockquote>
      <ul className="ph-comments-qmodal-meta">
        <li>
          ~{q.frequency_estimate ?? 1} mention{(q.frequency_estimate ?? 1) === 1 ? '' : 's'}
        </li>
        {(q.total_likes ?? 0) > 0 && <li>{q.total_likes} comment likes</li>}
        <li>{q.unanswered ? 'No creator reply yet' : 'Creator replied on-post'}</li>
        {creators && <li>Seen on: {creators}</li>}
      </ul>
      {(q.examples || []).length > 0 && (
        <div className="ph-comments-qmodal-examples">
          <p className="ph-comments-qmodal-examples-label">Similar comments</p>
          {(q.examples || []).map((ex) => (
            <p key={ex.slice(0, 40)} className="ph-cell-muted">
              “{ex}”
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PerfHubBuyerQuestionsModal({
  questions,
  campaignName,
  onClose,
  initialIndex = 0,
}) {
  const list = questions || [];
  const total = list.length;
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, initialIndex), Math.max(0, total - 1))
  );

  const goPrev = useCallback(() => {
    setIndex((i) => (i <= 0 ? total - 1 : i - 1));
  }, [total]);

  const goNext = useCallback(() => {
    setIndex((i) => (i >= total - 1 ? 0 : i + 1));
  }, [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!total) return null;

  const q = list[index];

  return (
    <div className="ph-comments-qmodal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ph-comments-qmodal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ph-comments-qmodal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ph-comments-qmodal-head">
          <div>
            <h3 id="ph-comments-qmodal-title">All buyer questions</h3>
            {campaignName && (
              <p className="ph-cell-muted ph-comments-qmodal-campaign">{campaignName}</p>
            )}
          </div>
          <button
            type="button"
            className="ph-comments-qmodal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <BuyerQuestionSlide q={q} index={index} total={total} />

        <footer className="ph-comments-qmodal-foot">
          <button type="button" className="btn-outline" onClick={goPrev} disabled={total <= 1}>
            ← Previous
          </button>
          <div className="ph-comments-qmodal-dots" aria-hidden>
            {list.map((item, i) => (
              <button
                key={item.question?.slice(0, 32) || i}
                type="button"
                className={`ph-comments-qmodal-dot${i === index ? ' active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to question ${i + 1}`}
              />
            ))}
          </div>
          <button type="button" className="btn-outline" onClick={goNext} disabled={total <= 1}>
            Next →
          </button>
        </footer>
      </div>
    </div>
  );
}
